/* eslint-disable no-unused-vars */
import { X } from 'lucide-react';
import React from 'react';
import { rollbackReleaseChannel } from '../../api';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useSession } from '../../contexts/SessionContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../../hooks/useActionDialog';
import { formatArtifactSummary } from '../../utils/formatters';

type RollbackDialogProps = {
  actionDialogReturn: UseActionDialogReturn;
  onNavigate: (path: string, opts?: { replace?: boolean }) => void;
};

function RollbackDialog({ actionDialogReturn, onNavigate: _onNavigate }: RollbackDialogProps) {
  const {
    actionDialog,
    dialogBusy,
    dialogError,
    setActionDialog,
    setDialogBusy,
    setDialogError,
  } = actionDialogReturn;

  const { session } = useSession();
  const { toast } = useFeedback();
  const {
    channels,
    sortedPromotions,
    artifactById,
    releaseAppById,
    releaseAppVersionById,
    setWorkspaceVersion,
  } = useWorkspace();

  if (!actionDialog || actionDialog.kind !== 'rollback') return null;

  const close = () => { if (!dialogBusy) setActionDialog(null); };

  const selectedRollbackChannel = channels.find(
    (ch) => ch.releaseChannelId === actionDialog.releaseChannelId
  ) ?? null;
  const lanePromotions = sortedPromotions.filter(
    (p) => p.releaseChannelId === selectedRollbackChannel?.releaseChannelId
  );
  const selectedVersion = selectedRollbackChannel?.releaseAppVersionId
    ? releaseAppVersionById.get(selectedRollbackChannel.releaseAppVersionId)
    : undefined;
  const selectedApp = selectedRollbackChannel?.releaseAppId
    ? releaseAppById.get(selectedRollbackChannel.releaseAppId)
    : selectedVersion
      ? releaseAppById.get(selectedVersion.releaseAppId)
      : undefined;
  const currentBundle = selectedRollbackChannel?.releaseArtifactId
    ? artifactById.get(selectedRollbackChannel.releaseArtifactId)
    : null;
  const previousPromotion = lanePromotions[1] ?? null;
  const previousBundle = previousPromotion
    ? artifactById.get(previousPromotion.releaseArtifactId)
    : null;

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session || !actionDialog || actionDialog.kind !== 'rollback' || dialogBusy) return;
    setDialogBusy(true);
    setDialogError('');
    try {
      const selectedChannel = channels.find(
        (ch) => ch.releaseChannelId === actionDialog.releaseChannelId
      );
      if (!selectedChannel) {
        setDialogError('Select a lane to roll back.');
        return;
      }
      await rollbackReleaseChannel(
        session.accessToken,
        selectedChannel.environment,
        selectedChannel.channel,
        { releaseChannelId: selectedChannel.releaseChannelId }
      );
      setWorkspaceVersion((v) => v + 1);
      toast('success', 'Release target restored to its previous bundle');
      setActionDialog(null);
    } catch (err: any) {
      setDialogError(err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.');
    } finally {
      setDialogBusy(false);
    }
  }

  return (
    <div className="dialogOverlay" role="presentation" onClick={close}>
      <form className="dialogPanel" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="dialogHeader">
          <div>
            <div className="panelKicker">Rollback</div>
            <h3>
              {selectedApp && selectedVersion
                ? `Restore ${selectedApp.name} ${selectedVersion.version}`
                : 'Restore release target'}
            </h3>
            <p>Return one exact target to its previously deployed bundle.</p>
          </div>
          <button type="button" className="iconButton" onClick={close} aria-label="Close dialog">
            <X size={16} />
          </button>
        </div>
        <div className="settingsForm">
          <label className="settingsField">
            <span>Release target</span>
            <select
              value={actionDialog.releaseChannelId}
              onChange={(e) => setActionDialog({ ...actionDialog, releaseChannelId: e.target.value })}
            >
              <option value="">— Choose a target —</option>
              {channels.map((ch) => (
                <option key={ch.releaseChannelId} value={ch.releaseChannelId}>
                  {(() => {
                    const version = ch.releaseAppVersionId
                      ? releaseAppVersionById.get(ch.releaseAppVersionId)
                      : undefined;
                    const app = ch.releaseAppId
                      ? releaseAppById.get(ch.releaseAppId)
                      : version
                        ? releaseAppById.get(version.releaseAppId)
                        : undefined;
                    return `${app?.name || 'Unknown app'} · ${
                      app?.platform?.toUpperCase() || 'Platform unavailable'
                    } · ${version?.version || ch.channel} · ${ch.environment}`;
                  })()}
                </option>
              ))}
            </select>
          </label>
          {selectedRollbackChannel ? (
            <div className="releaseConfirmation" aria-label="Rollback summary">
              <div className="releaseConfirmationRow">
                <span>App</span>
                <strong>{selectedApp?.name || 'Unknown app'}</strong>
              </div>
              <div className="releaseConfirmationRow">
                <span>Platform</span>
                <strong>{selectedApp?.platform?.toUpperCase() || 'Unavailable'}</strong>
              </div>
              <div className="releaseConfirmationRow">
                <span>Store version</span>
                <strong>{selectedVersion?.version || selectedRollbackChannel.channel}</strong>
              </div>
              <div className="releaseConfirmationRow">
                <span>Environment</span>
                <strong>{selectedRollbackChannel.environment}</strong>
              </div>
              <div className="releaseConfirmationRow">
                <span>Currently running</span>
                <strong>
                  {currentBundle ? formatArtifactSummary(currentBundle) : 'No bundle on this target'}
                </strong>
              </div>
              <div className="releaseConfirmationRow releaseConfirmationRow--change">
                <span>Will restore</span>
                <strong>
                  {previousBundle
                    ? formatArtifactSummary(previousBundle)
                    : previousPromotion
                      ? previousPromotion.releaseArtifactId
                      : 'No previous promotion on record'}
                </strong>
              </div>
            </div>
          ) : null}
        </div>
        {dialogError && <p className="fieldError">{dialogError}</p>}
        <div className="dialogActions">
          <button type="button" className="secondaryButton" onClick={close} disabled={dialogBusy}>Cancel</button>
          <button
            type="submit"
            className="primaryButton"
            disabled={dialogBusy || !selectedRollbackChannel || !previousPromotion}
          >
            {dialogBusy ? 'Restoring…' : 'Restore previous bundle'}
          </button>
        </div>
      </form>
    </div>
  );
}

export { RollbackDialog };
